import { useEffect, useRef } from 'react';
import { X, Bell, ChevronRight } from 'lucide-react';
import { cn } from '@/utils';
import { useGetNotificationsQuery, useMarkAllReadMutation } from '@/store/api/notificationApi';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface NotificationSidebarProps {
  open: boolean;
  onClose: () => void;
}

function getRoute(screen: string): string {
  return screen === 'Crypto Deposit Withdraw' ? '/requests/crypto' : '/pg-requests';
}

export function NotificationSidebar({ open, onClose }: NotificationSidebarProps) {
  const navigate = useNavigate();
  const [markAllRead] = useMarkAllReadMutation();
  const markedRef = useRef(false);

  const { data, isLoading } = useGetNotificationsQuery(undefined, {
    skip: !open,
  });

  useEffect(() => {
    if (open && !markedRef.current) {
      markedRef.current = true;
      markAllRead();
    }
    if (!open) {
      markedRef.current = false;
    }
  }, [open, markAllRead]);

  const notifications = data?.notifications ?? [];

  const handleNotifClick = (screen: string) => {
    navigate(getRoute(screen));
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 transition-opacity duration-300',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Sidebar panel */}
      <div
        className={cn(
          'fixed top-0 right-0 z-50 h-full w-[340px] flex flex-col transition-transform duration-300',
          'bg-[#0f0f1a] border-l-2 border-gold shadow-[−4px_0_24px_rgba(0,0,0,0.5)]',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-login-gradient">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-black" />
            <span className="text-black font-bold text-base tracking-wide">Notifications</span>
          </div>
          <button
            onClick={onClose}
            className="text-black/70 hover:text-black transition-colors rounded-full hover:bg-black/10 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Count bar */}
        {!isLoading && notifications.length > 0 && (
          <div className="px-5 py-2 bg-[#16162a] border-b border-white/10">
            <span className="text-xs text-white/70">
              {notifications.filter((n) => !n.read).length > 0
                ? `${notifications.filter((n) => !n.read).length} unread`
                : 'All caught up'}
            </span>
          </div>
        )}

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-40 gap-3">
              <div className="h-6 w-6 rounded-full border-2 border-gold border-t-transparent animate-spin" />
              <span className="text-white/50 text-sm">Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Bell className="h-10 w-10 text-white/30" />
              <span className="text-white/50 text-sm">No notifications yet</span>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    // onClick={() => handleNotifClick(n.scrren)}
                    className={cn(
                      'w-full text-left px-5 py-4 flex items-start gap-3 transition-colors',
                      'hover:bg-white/5 active:bg-white/10 cursor-default',
                      !n.read && 'bg-gold/5'
                    )}
                  >
                    {/* Unread dot */}
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
                        !n.read ? 'bg-gold' : 'bg-transparent'
                      )}
                    />

                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm leading-snug',
                          !n.read ? 'text-white font-semibold' : 'text-white/80 font-medium'
                        )}
                      >
                        {n.notification}
                      </p>
                      <p className="text-gold text-xs mt-1 font-medium">{n.scrren}</p>
                      <p className="text-white/50 text-xs mt-1">
                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                      </p>
                    </div>

                    <ChevronRight className="h-4 w-4 text-white/30 mt-1 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
